import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { FaCircle } from "react-icons/fa";
import { Disclosure } from "@headlessui/react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import tw from "tailwind-styled-components";

import Tooltip from "../Tooltip";
import LogoutConfirmModal from "./LogoutConfirmModal";
import FeedbackModal from "./FeedbackModal";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import { classNames } from "../../utils";
import HumanIcon from "../../assets/human-icon.png";
import { getIsSomeObjectPending } from "../../selectors/sync";

export const StatusWrapper = tw.div`
  m-2
  ml-auto
`;

const DropDownItem = tw.p`
block px-4 text-left py-2 text-sm text-gray-700 cursor-pointer
`;

export const Status = styled(({ ...props }) => {
  return <FaCircle className={props.className} />;
})`
  color: #f8ef49;
  opacity: ${(p) => (p.syncing ? 1 : 0)};
  transition: all 0.3s ease;
  ${(p) =>
    p.mobileOnly
      ? `
    margin: auto .75rem auto auto;
    @media (min-width: 768px) {
      display: none;
    }
  `
      : `
    @media (max-width: 767px) {
      display: none;
    }
  `}
`;

const LEFT_LINKS = [
  { name: "Notes", link: "/h/" },
  { name: "Timeline", link: "/t/" },
  { name: "Circles", link: "/c/" },
];

const LINKS_FOR_NON_LOGGEDIN_USERS = [
  { name: "Register", link: "/sign-up/" },
  { name: "Sign in", link: "/sign-in/" },
];

const StatusIcon = ({ syncing, mobileOnly = false }) =>
  syncing ? (
    <StatusWrapper>
      <Tooltip placement="bottom" tooltipChildren="There are unsynced changes">
        <Status mobileOnly={mobileOnly} syncing={syncing} />
      </Tooltip>
    </StatusWrapper>
  ) : null;

StatusIcon.propTypes = {
  syncing: PropTypes.any,
  mobileOnly: PropTypes.any,
};

const HeaderBar = (props) => {
  const PROFILE_DROPDOWN_LINKS = [
    { name: "Settings", link: "/settings/" },
    { name: "Help", link: "/help/" },
  ];
  const location = useLocation();
  const [openLogoutConfirmationModal, setOpenLogoutConfirmationModal] =
    useState(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);

  const { user, someObjectPending, history } = props;

  function onSignOut() {
    if (someObjectPending) {
      setOpenLogoutConfirmationModal(true);
    } else {
      history.push("/logout");
    }
  }
  return (
    <>
      <Disclosure
        as="nav"
        className="bg-gray-800 fixed w-full z-50 hidden lg:block"
      >
        {({ open }) => (
          <>
            <div className="max-w-5xl mx-auto px-2 sm:px-6 lg:px-8">
              <div className="relative flex items-center justify-between h-16">
                <div className="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start">
                  <Link
                    to="/h/"
                    className="flex-shrink-0 flex items-center cursor-pointer"
                  >
                    <p className="text-white font-bold">Humans</p>
                  </Link>
                  <div className="hidden sm:block sm:ml-6">
                    {user.isAuthenticated && (
                      <div className="flex space-x-4">
                        {LEFT_LINKS.map((item) => (
                          <Link
                            key={item.name}
                            to={item.link}
                            className={classNames(
                              location.pathname.indexOf(item.link) === 0
                                ? "bg-gray-900 text-white"
                                : "text-gray-300 hover:bg-gray-700 hover:text-white",
                              "px-3 py-2 rounded-md text-sm font-medium"
                            )}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                  <StatusIcon syncing={someObjectPending} />
                  {/* Profile dropdown */}
                  {user.isAuthenticated ? (
                    <>
                      <DropdownMenu
                        menuButton={
                          <img
                            className="h-8 w-8 rounded-full"
                            src={user.photo ? user.photo : HumanIcon}
                            alt=""
                          />
                        }
                        menuItems={PROFILE_DROPDOWN_LINKS}
                      >
                        {PROFILE_DROPDOWN_LINKS.map((item, key) => {
                          return (
                            <DropdownMenuItem key={key}>
                              <Link
                                to={item.link}
                                className={
                                  "block px-4 text-left py-2 text-sm text-gray-700"
                                }
                              >
                                {item.name}
                              </Link>
                            </DropdownMenuItem>
                          );
                        })}
                        <DropdownMenuItem>
                          <DropDownItem
                            onClick={() => setOpenFeedbackModal(true)}
                          >
                            Feedback
                          </DropDownItem>
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <DropDownItem onClick={onSignOut}>
                            Sign out
                          </DropDownItem>
                        </DropdownMenuItem>
                      </DropdownMenu>
                    </>
                  ) : (
                    <div className="hidden sm:block sm:ml-6">
                      <div className="flex space-x-4">
                        {LINKS_FOR_NON_LOGGEDIN_USERS.map((item) => (
                          <Link
                            key={item.name}
                            to={item.link}
                            className={classNames(
                              location.pathname.indexOf(item.link) === 0
                                ? "bg-gray-900 text-white"
                                : "text-gray-300 hover:bg-gray-700 hover:text-white",
                              "px-3 py-2 rounded-md text-sm font-medium"
                            )}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </Disclosure>
      {openLogoutConfirmationModal && (
        <LogoutConfirmModal
          closeConfirmModal={() => setOpenLogoutConfirmationModal(false)}
        />
      )}
      {openFeedbackModal && (
        <FeedbackModal
          closeFeedbackModal={() => {
            setOpenFeedbackModal(false);
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  someObjectPending: getIsSomeObjectPending(state),
});

HeaderBar.propTypes = {
  user: PropTypes.object.isRequired,
  someObjectPending: PropTypes.bool.isRequired,
  history: PropTypes.any,
};
export default connect(mapStateToProps)(HeaderBar);
